import React, { useState } from 'react';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';
import { US_STATES } from '../constants/usStates';
import { tsRowsForParcel, rolesForClient } from '../utils/assignments';

// Shared client-assignment UI, wired to client_role_assignments (the single source of truth for internal
// staff assigned to a client: Client Specialist / Tax Specialist / Client Manager). Reused by the Clients
// relationships screen (list cells + detail panel). Consultants are NOT here (external, property-level, own portal).
//   CS = one staff (all_states). CM = 1-2 staff (dropdown filtered to is_client_manager). TS = 1+ staff, each
//   All states (default) OR a specific-states set (the CBRE split). Saving a role clean-replaces the client's rows.
const STATE_CODES = Object.keys(US_STATES).sort();

const palette = (isDark) => isDark
  ? { card: 'rgba(47,56,84,0.95)', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc', input: 'var(--ink)', inputBorder: '#434C68', bubble: '#1a2238', bubbleBorder: '#434C68' }
  : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068', input: '#ffffff', inputBorder: '#c8ccd8', bubble: '#ffffff', bubbleBorder: '#c8ccd8' };

const mkStyles = (c) => ({
  input: { width: '100%', padding: '7px 10px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif" },
  btn: { padding: '7px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
  btnGhost: { padding: '6px 12px', background: 'transparent', border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.sub, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
  lbl: { fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px' },
});

// Clean-replace a client's rows for one role. Throws on error so the caller can surface it.
export async function saveClientRole(clientId, role, rows) {
  const del = await supabase.from('client_role_assignments').delete().eq('client_id', clientId).eq('role', role);
  if (del.error) throw del.error;
  const payload = rows
    .filter(r => r.staff_id && (r.all_states || (r.states && r.states.length)))
    .map(r => ({ client_id: clientId, role, staff_id: r.staff_id, all_states: !!r.all_states, states: r.all_states ? null : r.states, updated_at: new Date().toISOString() }));
  if (payload.length) {
    const ins = await supabase.from('client_role_assignments').insert(payload);
    if (ins.error) throw ins.error;
  }
}

// ── Reusable TS display: comma-joined names; a state-split TS gets a dotted underline + hover bubble of its states ──
export function TsAssignmentDisplay({ rows, staffName, darkMode, emptyText = '—' }) {
  const c = palette(darkMode);
  // 12px matches the CM(s)/CS cells these sit beside in every parcels/properties table; the surrounding
  // <td> is 14px/500, so a span that sets no size renders larger and heavier than its own neighbours.
  // Set HERE, not at the call site: StaffPortal:1773 used to override it on one <td>, which fixed that one
  // table and hid the same defect on three others.
  if (!rows || rows.length === 0) return <span style={{ fontSize: '12px', color: c.muted }}>{emptyText}</span>;
  return (
    <span style={{ fontSize: '12px' }}>
      {rows.map((r, i) => (
        <React.Fragment key={r.id || i}>
          {i > 0 && <span style={{ color: c.muted }}>, </span>}
          {r.all_states
            ? <span style={{ color: c.text }}>{staffName(r.staff_id)}</span>
            : <SplitTsName name={staffName(r.staff_id)} states={r.states || []} c={c} />}
        </React.Fragment>
      ))}
    </span>
  );
}

function SplitTsName({ name, states, c }) {
  const [hover, setHover] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <span style={{ color: c.text, borderBottom: '1px dotted var(--red)', cursor: 'default' }}>
        {name} <span style={{ color: 'var(--red)', fontWeight: 700 }}>({states.length})</span>
      </span>
      {hover && (
        <span style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '4px', zIndex: 10050, background: c.bubble, border: `1px solid ${c.bubbleBorder}`, borderRadius: '8px', boxShadow: '0 6px 20px rgba(0,0,0,0.25)', padding: '8px 10px', minWidth: '160px', whiteSpace: 'normal' }}>
          <span style={{ display: 'block', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: c.muted, marginBottom: '4px' }}>States</span>
          {states.length === 0
            ? <span style={{ fontSize: '12px', color: c.muted, fontStyle: 'italic' }}>None</span>
            : states.map(code => (
              <span key={code} style={{ display: 'block', fontSize: '12px', color: c.text }}>{code} — {US_STATES[code] || code}</span>
            ))}
        </span>
      )}
    </span>
  );
}

// ── Shared parcel-TS display: resolves a parcel's Tax Specialist(s) identically everywhere (Key Contacts,
//    parcels tables, etc.). Override wins → covered rows (split-aware, with hover-bubble) → coverage-gap flag → dash.
//    Coverage gap = the client HAS ≥1 TS assignment but none covers this parcel's state (and no override):
//    a genuine miss. Zero TS assignments (unassigned) or no state → plain dash, not a gap. ──
export function ParcelTsDisplay({ assignments, clientId, parcel, staffName, darkMode, emptyText = '—' }) {
  const c = palette(darkMode);
  const rows = tsRowsForParcel(assignments, clientId, parcel);
  if (rows.length) return <TsAssignmentDisplay rows={rows} staffName={staffName} darkMode={darkMode} emptyText={emptyText} />;
  const st = parcel && parcel.state;
  const clientHasTs = !!clientId && rolesForClient(assignments, clientId, 'TS').length > 0;
  if (st && clientHasTs) {
    return <span style={{ color: 'var(--red)', fontSize: '12px', fontWeight: 600 }}>No TS — {st} not covered</span>;
  }
  return <span style={{ fontSize: '12px', color: c.muted }}>{emptyText}</span>;
}

// ── Role popover — draft state per role, single Save clean-replaces the client's rows for that role ──
export default function AssignmentPopover({ role, clientName, staff, currentRows, onSave, onClose, darkMode }) {
  const c = palette(darkMode);
  const s = mkStyles(c);
  const isDark = darkMode;
  const ROLE_LABEL = { CS: 'Client Specialist', TS: 'Tax Specialist', CM: 'Client Manager(s)' };
  const [saving, setSaving] = useState(false);
  const [cs, setCs] = useState(currentRows[0]?.staff_id || '');                                   // CS: one staff
  const [cms, setCms] = useState(currentRows.map(r => r.staff_id));                               // CM: up to 2
  const [tsRows, setTsRows] = useState(                                                           // TS: 1+ rows
    currentRows.length ? currentRows.map(r => ({ staff_id: r.staff_id, all_states: !!r.all_states, states: r.states || [] }))
                       : [{ staff_id: '', all_states: true, states: [] }]);

  const cmEligible = staff.filter(x => x.is_client_manager);
  const doSave = async (rows) => { setSaving(true); await onSave(rows); };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      {...backdropProps(onClose)}>
      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.4)', padding: '20px 22px', width: role === 'TS' ? '520px' : '380px', maxWidth: '92vw', maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: c.text, marginBottom: '2px' }}>{ROLE_LABEL[role]}</div>
        <div style={{ fontSize: '12px', color: c.muted, marginBottom: '16px' }}>{clientName}</div>

        {role === 'CS' && (
          <div>
            <div style={s.lbl}>Client Specialist</div>
            <select value={cs} onChange={e => setCs(e.target.value)} style={s.input}>
              <option value="">— None —</option>
              {staff.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
            </select>
          </div>
        )}

        {role === 'CM' && (
          <div>
            <div style={s.lbl}>Client Manager(s) <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(1–2)</span></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {cms.length === 0 && <span style={{ fontSize: '12px', color: c.muted, fontStyle: 'italic' }}>None assigned.</span>}
              {cms.map(id => (
                <span key={id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.text, background: isDark ? 'rgba(180,0,0,0.15)' : 'rgba(180,0,0,0.06)', border: '1px solid var(--red)', borderRadius: '7px', padding: '4px 8px' }}>
                  {(cmEligible.find(x => x.id === id) || staff.find(x => x.id === id) || {}).name || id}
                  <button type="button" onClick={() => setCms(prev => prev.filter(x => x !== id))} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }}>✕</button>
                </span>
              ))}
            </div>
            {cms.length < 2 && (
              <select value="" onChange={e => { const id = e.target.value; if (id) setCms(prev => [...prev, id]); }} style={s.input}>
                <option value="">+ Add Client Manager…</option>
                {cmEligible.filter(x => !cms.includes(x.id)).map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
              </select>
            )}
          </div>
        )}

        {role === 'TS' && (
          <div>
            {tsRows.map((row, i) => (
              <div key={i} style={{ border: `1px solid ${c.border}`, borderRadius: '8px', padding: '12px', marginBottom: '10px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <div style={s.lbl}>Tax Specialist {tsRows.length > 1 ? `#${i + 1}` : ''}</div>
                    <select value={row.staff_id} onChange={e => setTsRows(prev => prev.map((r, j) => j === i ? { ...r, staff_id: e.target.value } : r))} style={s.input}>
                      <option value="">— Select staff —</option>
                      {staff.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
                    </select>
                  </div>
                  {tsRows.length > 1 && <button type="button" onClick={() => setTsRows(prev => prev.filter((_, j) => j !== i))} title="Remove" style={{ ...s.btnGhost, padding: '7px 9px' }}>✕</button>}
                </div>
                <div style={{ display: 'flex', gap: '16px', margin: '10px 0 4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.text, cursor: 'pointer' }}>
                    <input type="radio" checked={row.all_states} onChange={() => setTsRows(prev => prev.map((r, j) => j === i ? { ...r, all_states: true } : r))} /> All states
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.text, cursor: 'pointer' }}>
                    <input type="radio" checked={!row.all_states} onChange={() => setTsRows(prev => prev.map((r, j) => j === i ? { ...r, all_states: false } : r))} /> Specific states
                  </label>
                </div>
                {!row.all_states && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '4px', maxHeight: '160px', overflowY: 'auto', border: `1px solid ${c.inputBorder}`, borderRadius: '7px', padding: '8px', marginTop: '6px' }}>
                    {STATE_CODES.map(code => {
                      const on = (row.states || []).includes(code);
                      return (
                        <button key={code} type="button" title={US_STATES[code]}
                          onClick={() => setTsRows(prev => prev.map((r, j) => j === i ? { ...r, states: on ? r.states.filter(x => x !== code) : [...(r.states || []), code] } : r))}
                          style={{ fontSize: '11px', fontWeight: 600, padding: '4px 0', borderRadius: '5px', cursor: 'pointer', border: '1px solid ' + (on ? '#B40000' : c.inputBorder), background: on ? 'rgba(180,0,0,0.15)' : 'transparent', color: on ? 'var(--red)' : c.sub, fontFamily: "'Public Sans',system-ui,sans-serif" }}>
                          {code}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setTsRows(prev => [...prev, { staff_id: '', all_states: true, states: [] }])} style={{ ...s.btnGhost, fontSize: '12px' }}>+ Add Tax Specialist</button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <button type="button" style={s.btnGhost} disabled={saving} onClick={onClose}>Cancel</button>
          <button type="button" style={{ ...s.btn, opacity: saving ? 0.6 : 1 }} disabled={saving}
            onClick={() => {
              if (role === 'CS') doSave(cs ? [{ staff_id: cs, all_states: true }] : []);
              else if (role === 'CM') doSave(cms.map(id => ({ staff_id: id, all_states: true })));
              else doSave(tsRows.map(r => ({ staff_id: r.staff_id, all_states: r.all_states, states: r.states })));
            }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
